/**
 * Result-table columns as data, so the count is derived rather than retyped.
 *
 * `colSpan` on the expanded row, the empty state and the footer has to equal
 * the number of leaf columns. It was a hand-maintained constant per table
 * (`const COL_SPAN = 9` / `11`) — correct today, and silently wrong the first
 * time someone adds a column and does not think of it. The symptom is a
 * misaligned expanded row, which nothing fails on.
 *
 * Declaring the columns and deriving the number removes the second copy. The
 * ids are a literal union per table, so a cell rendered for a column that does
 * not exist does not compile either.
 */

/** One leaf column: what spans it in the header is a rendering concern. */
export interface ColumnDef<Id extends string> {
  id: Id;
  /** i18n key for the header label. Absent for the index / action columns,
   *  which render an icon or nothing. */
  labelKey?: string;
  /** Percentage width, when the table pins one. */
  width?: string;
}

/** The number a `colSpan` must use: leaf columns, not header cells. A
 *  two-row header groups some of them, and a group cell is not a column. */
export const colSpanOf = (columns: readonly ColumnDef<string>[]): number =>
  columns.length;

// ── technical-manual ────────────────────────────────────────────────────────

export type TmColumnId =
  | "index"
  | "sourcePath"
  | "value"
  | "itemMpListing"
  | "itemMpId"
  | "targetValue"
  | "status"
  | "actions"
  | "expand";

export const TM_COLUMNS: readonly ColumnDef<TmColumnId>[] = [
  { id: "index" },
  { id: "sourcePath", labelKey: "jobResultsPage.tmTable.sourcePath" },
  { id: "value", labelKey: "jobResultsPage.tmTable.value" },
  { id: "itemMpListing", labelKey: "jobResultsPage.tmTable.itemMpListing" },
  { id: "itemMpId", labelKey: "jobResultsPage.tmTable.itemMpId" },
  { id: "targetValue", labelKey: "jobResultsPage.tmTable.targetValue" },
  { id: "status", labelKey: "jobResultsPage.columns.status" },
  { id: "actions", labelKey: "jobResultsPage.columns.actions" },
  { id: "expand" },
] as const;

// ── OE (wd-mapping) ────────────────────────────────────────────────────────

export type OeColumnId =
  | "index"
  | "oeNumber"
  | "oeName"
  | "oeFunction"
  | "oeDomain"
  | "itemMpId"
  | "itemMpText"
  | "status"
  | "actions";

export const OE_COLUMNS: readonly ColumnDef<OeColumnId>[] = [
  { id: "index", width: "3%" },
  { id: "oeNumber", labelKey: "jobResultsPage.wdTable.number", width: "7%" },
  { id: "oeName", labelKey: "jobResultsPage.wdTable.text", width: "23%" },
  { id: "oeFunction", labelKey: "jobResultsPage.wdTable.function", width: "16%" },
  { id: "oeDomain", labelKey: "jobResultsPage.wdTable.domain", width: "9%" },
  { id: "itemMpId", labelKey: "jobResultsPage.wdTable.itemMpId", width: "6%" },
  { id: "itemMpText", labelKey: "jobResultsPage.wdTable.itemMpText", width: "19%" },
  { id: "status", labelKey: "jobResultsPage.columns.status", width: "10%" },
  { id: "actions", labelKey: "jobResultsPage.columns.actions", width: "7%" },
] as const;
